import { useRef, useState, useEffect } from "react";
import type { ThroughlineArcPoint, ThroughlineNodeState } from "@/lib/throughlineAdapter";

type ThroughlinePulseChartProps = {
  points: ThroughlineArcPoint[];
  chartLabelPoints: ThroughlineArcPoint[];
  selectedDayKey: string | null;
  onSelectDay: (dayKey: string) => void;
};

const CHART_HEIGHT = 224;
const DEFAULT_WIDTH = 800;

function throughlineDayKeyFromPointId(pointId: string) {
  return pointId.startsWith("bucket-") ? pointId.replace("bucket-", "") : null;
}

function getProgressColor(progress: number) {
  if (progress < 0.2) return "#60a5fa";
  if (progress < 0.4) return "#a78bfa";
  if (progress < 0.6) return "#22d3ee";
  if (progress < 0.8) return "#fbbf24";
  return "#fb7185";
}

function getThroughlineStateColor(state: ThroughlineNodeState, isEmpty?: boolean) {
  if (isEmpty) return "#8b6ff0";
  if (state === "healthy") return "#34d399";
  if (state === "stalled") return "#fbbf24";
  return "#fb7185";
}

function useContainerWidth(ref: React.RefObject<HTMLDivElement | null>) {
  const [width, setWidth] = useState(DEFAULT_WIDTH);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setWidth(Math.round(w));
    };

    measure();

    if (typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return width;
}

/** Map a point's normalized x (0–100) and y into pixel coordinates with adaptive insets */
function toPixel(point: { x: number; y: number }, width: number, inset: number) {
  const usable = width - 2 * inset;
  const px = inset + (point.x / 100) * usable;
  const normalizedY = point.y / 2;
  const py = 20 + ((normalizedY - 9.5) / 11) * 184;
  return { px, py };
}

function yToPixel(y: number) {
  const normalizedY = y / 2;
  return 20 + ((normalizedY - 9.5) / 11) * 184;
}

export function ThroughlinePulseChart({
  points,
  chartLabelPoints,
  selectedDayKey,
  onSelectDay,
}: ThroughlinePulseChartProps) {
  const chartSurfaceClassName =
    "rounded-lg border border-border/60 bg-card/80 dark:border-violet-300/20 dark:bg-black/20";
  const guideStroke = "hsl(var(--border) / 0.7)";
  const axisTextFill = "hsl(var(--muted-foreground) / 0.95)";
  const emptyNodeFill = "hsl(var(--card))";
  const nodeGradientCenter = "hsl(var(--card))";
  const nodeGradientOuter = "hsl(var(--background))";
  const areaFillColor = "hsl(var(--primary) / 0.14)";
  const containerRef = useRef<HTMLDivElement>(null);
  const width = useContainerWidth(containerRef);
  const isCompact = width < 640;
  const isVeryCompact = width < 480;
  const midY = CHART_HEIGHT / 2;
  const highY = yToPixel(20);
  const mediumY = yToPixel(30);
  const lowY = yToPixel(40);

  // Adaptive inset: more padding for sparse data, zero for dense
  const inset = Math.max(0, (width * 0.04) * Math.max(0, 1 - points.length / 30));
  const visibleLabelPoints = chartLabelPoints.filter((_, index, arr) => {
    if (arr.length <= 2) return true;
    if (index === 0 || index === arr.length - 1) return true;
    if (isVeryCompact) return index % 4 === 0;
    if (isCompact) return index % 2 === 0;
    return true;
  });

  return (
    <>
      <div ref={containerRef} className="w-full">
        <svg
          width="100%"
          height={CHART_HEIGHT}
          viewBox={`0 0 ${width} ${CHART_HEIGHT}`}
          preserveAspectRatio="xMidYMid meet"
          className={chartSurfaceClassName}
          shapeRendering="geometricPrecision"
        >
          <defs>
            <linearGradient id="throughline-spine" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.55" />
              <stop offset="45%" stopColor="#a855f7" stopOpacity="0.78" />
              <stop offset="75%" stopColor="#22d3ee" stopOpacity="0.92" />
              <stop offset="100%" stopColor="#fb7185" stopOpacity="1" />
            </linearGradient>
            <radialGradient id="throughline-node-fill" cx="50%" cy="45%" r="65%">
              <stop offset="0%" stopColor={nodeGradientCenter} stopOpacity="0.96" />
              <stop offset="100%" stopColor={nodeGradientOuter} stopOpacity="0.92" />
            </radialGradient>
            {/* SVG filter for selected node glow */}
            <filter id="selected-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Vertical-axis reference guides */}
          <line x1="4" y1={highY} x2={width - 4} y2={highY} stroke={guideStroke} strokeWidth="0.8" strokeDasharray="4 8" opacity="0.7" />
          <line x1="4" y1={mediumY} x2={width - 4} y2={mediumY} stroke={guideStroke} strokeWidth="1" strokeDasharray="4 6" opacity="0.85" />
          <line x1="4" y1={lowY} x2={width - 4} y2={lowY} stroke={guideStroke} strokeWidth="0.8" strokeDasharray="4 8" opacity="0.7" />
          <line x1="0" y1={midY} x2={width} y2={midY} stroke="url(#throughline-spine)" strokeWidth="0.5" opacity="0.3" />
          <text x="10" y={highY - 6} fill={axisTextFill} fontSize={isVeryCompact ? "9" : "10"}>High</text>
          <text x="10" y={mediumY - 6} fill={axisTextFill} fontSize={isVeryCompact ? "9" : "10"}>Medium</text>
          <text x="10" y={lowY - 6} fill={axisTextFill} fontSize={isVeryCompact ? "9" : "10"}>Low</text>

          {/* Area fill and line segments */}
          {(() => {
            const plotted = points.map((point) => {
              const { px, py } = toPixel(point, width, inset);
              return { ...point, px, py };
            });
            if (plotted.length === 0) return null;

            const withEdges = [
              { ...plotted[0], px: inset, py: plotted[0].py },
              ...plotted,
              { ...plotted[plotted.length - 1], px: width - inset, py: plotted[plotted.length - 1].py },
            ];

            const areaPathD =
              `M ${withEdges[0].px} ${withEdges[0].py}` +
              withEdges.slice(1).map((p) => ` L ${p.px} ${p.py}`).join("") +
              ` L ${width - inset} ${CHART_HEIGHT} L ${inset} ${CHART_HEIGHT} Z`;

            return (
              <>
                <defs>
                  <linearGradient id="throughline-area-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={areaFillColor} stopOpacity="1" />
                    <stop offset="100%" stopColor={areaFillColor} stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d={areaPathD} fill="url(#throughline-area-fill)" />

                {withEdges.slice(0, -1).map((point, index) => {
                  const next = withEdges[index + 1];
                  const segmentColor = getProgressColor(next.progress);
                  return (
                    <line
                      key={`${point.id}-segment-${index}`}
                      x1={point.px}
                      y1={point.py}
                      x2={next.px}
                      y2={next.py}
                      stroke={segmentColor}
                      strokeWidth={2}
                      opacity={point.isEmpty && next.isEmpty ? 0.95 : 1}
                    />
                  );
                })}
              </>
            );
          })()}

          {/* Nodes */}
          {points.map((point) => {
            const { px, py } = toPixel(point, width, inset);
            const stroke = getThroughlineStateColor(point.state, point.isEmpty);
            const progressColor = getProgressColor(point.progress);
            const dayKey = throughlineDayKeyFromPointId(point.id);
            const isSelected = Boolean(dayKey && selectedDayKey === dayKey);

            // Capped node sizes
            const nodeRadius = point.isEmpty ? 4 : 5 + point.evidence * 3;
            const interactionRadius = Math.max(nodeRadius + 8, 22);

            return (
              <g
                key={point.id}
                role="button"
                tabIndex={0}
                className="cursor-pointer"
                onClick={() => dayKey && onSelectDay(dayKey)}
                onKeyDown={(event) => {
                  if (!dayKey) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelectDay(dayKey);
                  }
                }}
              >
                <title>
                  {`${point.label} · ${point.count} events · Evidence ${Math.round(point.evidence * 100)}% · Agreement ${Math.round(point.agreement * 100)}%`}
                </title>

                <circle
                  cx={px}
                  cy={py}
                  r={interactionRadius}
                  fill="transparent"
                  stroke="none"
                />

                {/* Outer pulsing ring (selected only) — SVG native animate */}
                {isSelected && (
                  <circle
                    cx={px}
                    cy={py}
                    r={nodeRadius + 5}
                    fill="none"
                    stroke={progressColor}
                    strokeWidth={0.8}
                    opacity={0.2}
                  >
                    <animate
                      attributeName="r"
                      values={`${nodeRadius + 4};${nodeRadius + 7};${nodeRadius + 4}`}
                      dur="2s"
                      repeatCount="indefinite"
                    />
                  </circle>
                )}

                {/* Inner pulsing ring (selected only) */}
                {isSelected && (
                  <circle
                    cx={px}
                    cy={py}
                    r={nodeRadius + 2}
                    fill="none"
                    stroke={progressColor}
                    strokeWidth={1.2}
                    opacity={0.45}
                  >
                    <animate
                      attributeName="r"
                      values={`${nodeRadius + 1.5};${nodeRadius + 3};${nodeRadius + 1.5}`}
                      dur="2s"
                      repeatCount="indefinite"
                    />
                  </circle>
                )}

                {/* Subtle glow circle (non-selected only) */}
                {!isSelected && (
                  <circle
                    cx={px}
                    cy={py}
                    r={nodeRadius + 1}
                    fill={progressColor}
                    opacity={point.isEmpty ? 0.08 : 0.12}
                  />
                )}

                {/* Main node */}
                <circle
                  cx={px}
                  cy={py}
                  r={nodeRadius}
                  fill={point.isEmpty ? emptyNodeFill : "url(#throughline-node-fill)"}
                  stroke={progressColor}
                  strokeWidth={isSelected ? 2 : point.isEmpty ? 1 : 1.5}
                  strokeDasharray={point.isEmpty ? "4 3" : undefined}
                  opacity={point.isEmpty ? 0.95 : 1}
                  filter={isSelected ? "url(#selected-glow)" : undefined}
                />

                {/* Inner state ring */}
                {!point.isEmpty && (
                  <circle
                    cx={px}
                    cy={py}
                    r={3 + point.evidence * 2}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={1.5}
                  />
                )}

                {/* Detail arcs */}
                {!point.isEmpty && !isCompact && (
                  <>
                    <path
                      d={`M ${px - 5} ${py - 3} A 5 5 0 0 1 ${px + 5} ${py + 3}`}
                      stroke="#2dd4bf"
                      strokeWidth={1}
                      fill="none"
                      opacity={0.98}
                    />
                    <path
                      d={`M ${px + 5} ${py + 3} A 5 5 0 0 1 ${px - 5} ${py - 3}`}
                      stroke="#fb923c"
                      strokeWidth={1}
                      fill="none"
                      opacity={0.98}
                    />
                  </>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      <div className="grid gap-2 text-[11px] text-muted-foreground dark:text-violet-200/80 lg:grid-cols-3">
        <p>
          <span className="font-semibold text-foreground dark:text-violet-100">Node size:</span> stronger evidence on that day.
        </p>
        <p>
          <span className="font-semibold text-foreground dark:text-violet-100">Outer color:</span> time progression from left to right.
        </p>
        <p>
          <span className="font-semibold text-foreground dark:text-violet-100">Vertical position:</span> daily behavior signal (`High` to `Low`).
        </p>
      </div>
      <div className={isVeryCompact ? "relative h-6" : "relative h-5"}>
        {visibleLabelPoints.map((point, index) => (
          <span
            key={`${point.id}-label`}
            className="absolute max-w-[4.5rem] -translate-x-1/2 truncate text-[10px] text-muted-foreground dark:text-violet-300/80 sm:max-w-none sm:text-[11px] sm:whitespace-nowrap"
            style={{
              left: `${point.x}%`,
              transform:
                index === 0
                  ? "translateX(0)"
                  : index === visibleLabelPoints.length - 1
                    ? "translateX(-100%)"
                    : "translateX(-50%)",
            }}
          >
            {point.label}
          </span>
        ))}
      </div>
    </>
  );
}
